/**
 * Orders
 */

// React
import React, {FunctionComponent, useEffect, useState, useContext} from 'react';
import {useForm} from 'react-hook-form';

// Context
import AuthContext, {AuthConsumer} from '../../authContext';

// Redux
import {connect} from 'react-redux';
import {compose} from 'redux';
import {
  getOrdersAction,
  updateOrderStatusActions,
  updateAllOrderStatusActions,
  actionRetrieveRubikaOrdersAction,
  cancelOrderAction,
  getAllOrdersReportAction,
} from './actions';
import {Store} from '../../redux/reducers';

// Selector
import {createStructuredSelector} from 'reselect';
import {
  makeSelectOrdersList,
  makeSelectLoading,
  makeSelectError,
  makeSelectCountList,
  makeSelectAllOrders,
} from './selectors';

// Router
import {useParams} from 'react-router-dom';

// UI
import clsx from 'clsx';
import theme from '../../theme';
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import {
  CardHeader,
  CardContent,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  InputBase,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  ClickAwayListener,
  Paper,
} from '@material-ui/core';

import List from './list';
import Pagination from '@material-ui/lab/Pagination';
import Can from '../../components/Can';
import CSV from './csv';

// Helper
import {
  removeEmptyValueObject,
  translate,
  checkMatchMedia,
} from '../../utils/helpers';

// Type
import {
  Props,
  SelectorProps,
  Payload,
  Status,
  cancelPayload,
  PayloadAllUpdate,
} from './interfaces';
import RonadIcons from '../../components/iconComponent';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      marginRight: '140px',
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      // boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      border: 'solid 1px #8a8a8a',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '12px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      marginTop: '14px',
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    typographyCards: {
      fontSize: '16px',
      color: 'white',
      backgroundColor: '#4BCF90',
      padding: '8px 24px 8px 50px',
      marginLeft: '-40px',
    },
    card: {
      width: '100%',
      marginBottom: '14px',
      overflow: 'visible',
      padding: '14px 0 0',
      paddingBottom: '0',
    },

    cardContent: {
      justifyContent: 'space-between',
      padding: ' 0',
    },
    cardContentOrder: {
      padding: '0',
    },
    headerBox: {
      padding: '5px',

      marginLeft: '75px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      marginTop: '10px',
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};

const VariantOrders: FunctionComponent<Props> = (props: Props) => {
  const [filtersValues, setFiltersValues] = useState({
    'itemsSkuId.equals': props.match?.params?.id,
    page: 0,
    size: 10,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [cancelDescription, setCancelDescription] = useState('' as string);
  const [lastUpdate, setLastUpdate] = useState(
    '2019-07-02T17:15:03+04:30' as string
  );

  // Bad Bad Bad.. :(
  props.ordersList[0]?.created &&
    props.ordersList[0]?.created > lastUpdate &&
    setLastUpdate(props.ordersList[0]?.created);

  useEffect(() => {
    getOrders();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({...filtersValues, page: page - 1});
  };

  const getOrders = () => {
    const {page, size} = filtersValues;
    props.ordersDispatch({
      ...filtersValues,
      page,
      size,
    });
  };

  const handleCancelOrder = async () => {
    await props.cancelOrderDispatch({
      id,
      body: {description: cancelDescription},
    });
    getOrders();
    setOpenConfirmCancel(false);
  };

  const changeStatusOrder = () => {
    props.updateOrderStatusDispatch({
      id,
      body: previewState,
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
  };
  const changeStatusOrderWhitId = (data: any) => {
    const {ids, status, description} = data;
    props.updateAllOrderStatusDispatch({
      ids,
      status,
      description,
      pagination: filtersValues,
    });

    // props.updateOrderStatusDispatch({
    //   id,
    //   body,
    //   pagination: filtersValues,
    // });
    setOpenConfirmUpdateState(false);
  };

  const handleOpenConfirmCancele = (id: number) => {
    setOpenConfirmCancel(true);
    setId(id);
  };
  const handleOpenConfirmUpdateState = (data: {id: number; body: Status}) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
  };

  const findOrderStatus = (id: number) => {
    const status = props.ordersList.find(order => order.id === id)?.status;
    return status && translate(status, 'order');
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{value: unknown}>) => {
    setFiltersValues({...filtersValues, page: 0, size: event.target.value});
  };

  return (
    <AuthConsumer>
      {({user}) => (
        <div className={classes.root}>
          <Card className={classes.card}>
            <CardHeader
              avatar={
                <Grid>
                  <Typography className={classes.typographyCards}>
                    لیست سفارش‌ها
                  </Typography>
                </Grid>
              }
              action={
                <div className={classes.btnBox}>
                  <Can
                    role={user.role}
                    perform="orders:btnGetNewOrders"
                    yes={() => (
                      <>
                        <Button
                          variant="contained"
                          color="secondary"
                          size={
                            checkMatchMedia('(max-width: 1024px)')
                              ? 'small'
                              : 'large'
                          }
                          onClick={() =>
                            props.actionRetrieveRubikaOrdersDispatch({})
                          }
                        >{`دریافت سفارش جدید`}</Button>
                      </>
                    )}
                  />

                  <Can
                    role={user.role}
                    perform="orders:downloadReportCSV"
                    yes={() => (
                      <>
                        <CSV
                          ordersDispatch={props.allOrdersDispatch}
                          data={props.allOrders}
                          filtersValues={filtersValues}
                          loading={props.loading}
                        />
                      </>
                    )}
                  />
                </div>
              }
            />

            <CardContent className={classes.cardContentOrder}>
              <Grid container lg={12} md={12} className={classes.cardContent}>
                <Grid container item lg={12} md={12}>
                  <Grid
                    container
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    alignItems="center"
                  >
                    {props.loading ? (
                      <div className={classes.loading}>
                        <CircularProgress />
                      </div>
                    ) : (
                      <>
                        {props.ordersList.length > 0 ? (
                          <List
                            data={props.ordersList}
                            handleConfirmCanceleBox={handleOpenConfirmCancele}
                            changeStatusOrderWhitId={changeStatusOrderWhitId}
                            handleOpenConfirmUpdateState={
                              handleOpenConfirmUpdateState
                            }
                          />
                        ) : (
                          <Typography variant="h3" component="div">
                            سفارشی موجود نیست
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>
                  <Grid md={12} container style={{padding: '24px 16px 0 10px'}}>
                    <Grid item xs={6} sm={6}>
                      {props.ordersList.length > 0 && (
                        <FormControl
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{marginLeft: '10px', color: '#8a8a8a'}}>
                            {' '}
                            تعداد نمایش در هر صفحه:
                          </div>
                          <Select
                            open={openSize}
                            onClose={handleClose}
                            onOpen={() => setOpenSize(true)}
                            value={filtersValues.size}
                            onChange={handleChangeSizePage}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                    <Grid xs={6} sm={6} container item direction="row-reverse">
                      {props.ordersList.length > 0 && (
                        <Pagination
                          className={classes.pagination}
                          count={Math.ceil(props.count / filtersValues.size)}
                          color="secondary"
                          page={filtersValues.page + 1}
                          onChange={handleChangePage}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Dialog
            open={openConfirmCancel}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <RonadIcons name="times"  size={0.8}/>
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <RonadIcons name="times" className='pointer text-danger'  size={3.5}/>
              </div>
              <div
                className={classes.cancelTitle}
              >{`از لغو سفارش با شناسه ${id} مطمئن هستید؟`}</div>
              <TextField
                className={classes.textArea}
                placeholder={`علت لغو...`}
                value={cancelDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCancelDescription(e.target.value)
                }
              />
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCancelOrder}
                    disabled={!(cancelDescription.length > 0)}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <RonadIcons name="times"  size={0.8}/>
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
              <RonadIcons name='pencil' size={4} className="text-primary"/>
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translate(previewState.replace(/"/g, '') as Status, 'order')}"

 
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </AuthConsumer>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  ordersList: makeSelectOrdersList(),
  allOrders: makeSelectAllOrders(),
  count: makeSelectCountList(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    ordersDispatch: (payload: Payload) => dispatch(getOrdersAction(payload)),
    allOrdersDispatch: (payload: Payload) =>
      dispatch(getAllOrdersReportAction(payload)),
    updateOrderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateOrderStatusActions(payload)),
    updateAllOrderStatusDispatch: (payload: PayloadAllUpdate) =>
      dispatch(updateAllOrderStatusActions(payload)),
    actionRetrieveRubikaOrdersDispatch: (payload: {
      date?: String;
      from?: number;
      to?: number;
    }) => dispatch(actionRetrieveRubikaOrdersAction(payload)),
    cancelOrderDispatch: (payload: cancelPayload) =>
      dispatch(cancelOrderAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(VariantOrders);
